import { useMemo, useState } from "react";

import { useCurrentUser } from "@/features/auth/providers/CurrentUser";
import { usePlayerSearchListAvailableCustomStatsQuery } from "@/generated/graphql";

/**
 *
 */
interface AvailableCustomMetric {
  id: string;
  displayName: string;
  minValue: number;
  maxValue: number;
  defaultMin: number;
  defaultMax: number;
  step: number;
}

/**
 * Options for {@link useAvailableCustomMetrics}.
 */
interface UseAvailableCustomMetricsOptions {
  /**
   * Whether the caller needs the list now. Defaults to true. A surface that
   * mounts closed passes false until it opens, so it sends no request.
   */
  readonly enabled?: boolean;
}

/**
 * Reads the organization's custom stats as filter/sort metrics.
 *
 * `PlayerSearchListAvailableCustomStats` can take 20 s on a cold replica
 * (SE-8829), and the player profile mounts closed sort and filter surfaces, so
 * a caller can defer the read with `enabled: false` (SE-8832). Once enabled
 * the read stays on for the caller's lifetime: closing a surface must not
 * abandon a slow read in flight or blank the list while the surface animates
 * out.
 * @param options - See {@link UseAvailableCustomMetricsOptions}
 * @param options.enabled - Whether the caller needs the list now
 * @returns The metrics, the query's loading state, and whether any exist
 */
export const useAvailableCustomMetrics = ({
  enabled = true,
}: UseAvailableCustomMetricsOptions = {}) => {
  const currentUser = useCurrentUser();
  const [wasEnabled, setWasEnabled] = useState(enabled);
  if (enabled && !wasEnabled) {
    setWasEnabled(true);
  }

  // Fetch available custom stats from the backend
  const { data, loading } = usePlayerSearchListAvailableCustomStatsQuery({
    skip: !currentUser?.organization?.id || !(enabled || wasEnabled),
  });

  const availableCustomMetrics = useMemo((): AvailableCustomMetric[] => {
    if (!data?.listAvailableCustomStats || loading) {
      return [];
    }

    // Convert backend custom stat definitions to frontend format
    return data.listAvailableCustomStats
      .map(stat => {
        if (!stat?.name) return null;

        // Convert BACKEND_KEY to Title Case display name
        // (e.g., "CURRENT_SEASON_GPR" -> "Current Season Gpr")
        const displayName = stat.name
          .split("_")
          .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(" ");

        return {
          id: stat.name,
          displayName,
          minValue: 0, // Use standard 0-100 range for custom metrics
          maxValue: 100,
          defaultMin: 0, // Default to full range when no filters applied
          defaultMax: 100,
          step: 1, // Use whole numbers for custom metrics
        };
      })
      .filter((metric): metric is AvailableCustomMetric => metric !== null)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [data, loading]);

  return {
    availableCustomMetrics,
    loading,
    hasCustomMetrics: availableCustomMetrics.length > 0,
  };
};
